
import React, { useLayoutEffect } from "react";
import _ from "lodash";

import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { getPieChart, addLicense } from "../../../components/Charts/commons";

export const GameSurveysDonutChart = ({
  id,
  data,
  intl,
  worksiteMeasure,
  currency = "EUR",
  totalPlacement = "center", // "top" | "center"
  outerRadiusPercent = 80, // controls overall donut size
  innerRadiusPercent = 55, // controls donut thickness (lower = thicker)
}) => {
  const surveys = intl.formatMessage({ id: "gameSurveys" });

  useLayoutEffect(() => {
    addLicense();

    const root = am5.Root.new(id);
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    const chart = getPieChart(root);

    // If we want to place the total label above the chart,
    // make the chart use a vertical layout so the label stacks above the plot.
    if (totalPlacement === "top") {
      chart.set("layout", root.verticalLayout);
    }

    const isCurrency = worksiteMeasure === "amt";
    const formatValue = (v) =>
      isCurrency
        ? intl.formatNumber(v, {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        : intl.formatNumber(v, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });

    const totalValue = (data || []).reduce((acc, i) => acc + (i?.value || 0), 0);
    const formattedTotalValue = formatValue(totalValue);

    const dataWithDisplay = (data || []).map((d) => ({
      ...d,
      valueDisplay: formatValue(d.value),
    }));

    // Add series
    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: "value",
        categoryField: "category",
        alignLabels: false,
        // Make donut size configurable
        radius: am5.percent(outerRadiusPercent),
        innerRadius: am5.percent(innerRadiusPercent),
      })
    );

    series.slices.template.setAll({
      templateField: "settings",
      fillOpacity: 0.7,
      tooltipText: `[bold]{category}[/]\n {valueDisplay} ${surveys} \n {valuePercentTotal.formatNumber('0.')}%`,
    });

    // Labels: show category + percentage (unchanged)
    series.ticks.template.set("visible", false);
    series.labels.template.setAll({
      maxWidth: 120,
      oversizedBehavior: "truncate",
      text:
        "[bold fontSize:10px]{category}[/]\n[fontSize:14px]{valuePercentTotal.formatNumber('0.')}%[/][/]",
    });

    series.data.setAll(dataWithDisplay);

    // Total label placement
    if (totalPlacement === "top") {
      // Add a label ABOVE the chart using the chart's vertical layout
      chart.children.unshift(
        am5.Label.new(root, {
          textAlign: "center",
          centerX: am5.p50,
          // leave some spacing below the label so it doesn't touch the donut
          paddingBottom: 8,
          text: `[bold fontSize:20px]${formattedTotalValue}[/]`,
        })
      );
    } else {
      // Center label (keep it in the middle). To make it feel roomier,
      // you can also increase the outer radius or reduce the inner radius above.
      chart.seriesContainer.children.push(
        am5.Label.new(root, {
          textAlign: "center",
          centerY: am5.p50,
          centerX: am5.p50,
          text: `[bold fontSize:24px]${formattedTotalValue}[/]`,
        })
      );
    }

    // Animate
    series.appear(1000);
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [
    data,
    id,
    surveys,
    intl,
    worksiteMeasure,
    currency,
    totalPlacement,
    outerRadiusPercent,
    innerRadiusPercent,
  ]);

  return <div id={id} style={{ width: "100%", height: "300px" }} />;
};
